/**
 * Parsed from TenantBranding.pdfLayoutJson — drives invoice/quote PDF chrome (Playwright HTML).
 */

export type PdfExtraLogo = {
  id: string;
  /** Embedded image (`data:image/...;base64,...`). */
  dataUrl: string;
  xFrac: number;
  yFrac: number;
  slotWidthFrac: number;
};

export type LogoAlign = 'left' | 'center' | 'right';

export type PdfTableColumnKey =
  | 'item'
  | 'description'
  | 'quantity'
  | 'unitPrice'
  | 'amount'
  | 'custom';

export type PdfTableColumn = {
  id: string;
  key: PdfTableColumnKey;
  label: string;
  align?: 'left' | 'right' | 'center';
  /** When key is `item`, show description under the name. */
  showDescriptionUnderItem?: boolean;
  /** Static cell text when key is `custom`. */
  customText?: string;
};

export type PdfBodySectionKind =
  | 'title'
  | 'meta'
  | 'itemsTable'
  | 'totals'
  | 'intro'
  | 'signature'
  | 'disclaimer'
  | 'compliance'
  | 'footerStrip'
  | 'customText';

export type PdfBodySection = {
  id: string;
  kind: PdfBodySectionKind;
  enabled?: boolean;
  /** Optional heading for customText / intro / signature sections. */
  title?: string;
  /** Plain text body for customText sections. */
  text?: string;
};

export const PDF_LAYOUT_MAX_TABLE_COLUMNS = 8;
export const PDF_LAYOUT_MAX_BODY_SECTIONS = 16;

export const DEFAULT_TABLE_COLUMNS: PdfTableColumn[] = [
  {
    id: 'col-item',
    key: 'item',
    label: 'Item',
    align: 'left',
    showDescriptionUnderItem: true
  },
  { id: 'col-qty', key: 'quantity', label: 'Qty', align: 'right' },
  { id: 'col-rate', key: 'unitPrice', label: 'Rate', align: 'right' },
  { id: 'col-amount', key: 'amount', label: 'Amount', align: 'right' }
];

export const DEFAULT_BODY_SECTIONS: PdfBodySection[] = [
  { id: 'sec-title', kind: 'title', enabled: true },
  { id: 'sec-meta', kind: 'meta', enabled: true },
  { id: 'sec-items', kind: 'itemsTable', enabled: true },
  { id: 'sec-totals', kind: 'totals', enabled: true },
  { id: 'sec-intro', kind: 'intro', enabled: true },
  { id: 'sec-signature', kind: 'signature', enabled: true, title: 'Authorization' },
  { id: 'sec-disclaimer', kind: 'disclaimer', enabled: true },
  { id: 'sec-compliance', kind: 'compliance', enabled: true },
  { id: 'sec-footer-strip', kind: 'footerStrip', enabled: true }
];

export interface PdfCustomLayout {
  v: 1;
  logo: {
    /** 0–1 horizontal position inside header (from left). */
    xFrac: number;
    /** 0–1 vertical position inside header (from top). */
    yFrac: number;
    /** Slot width as fraction of full page width (~794px PDF). */
    slotWidthFrac: number;
    /** Structured position — overrides xFrac when set. */
    align?: LogoAlign;
  };
  badges: {
    /** Horizontal inset from right edge of header as fraction (0–1). */
    rightInsetFrac: number;
    /** Vertical offset from header top as fraction of header height (0–1). */
    yFrac: number;
    gapPx: number;
    showCfaa?: boolean;
    showAcmo?: boolean;
  };
  stripe?: {
    show?: boolean;
  };
  body?: {
    rowAltBg?: string;
    tableColumns?: PdfTableColumn[];
    sections?: PdfBodySection[];
    sectionOrder?: string[];
  };
  footer?: {
    showLogoStrip?: boolean;
    /** Disclaimer / footer note text color (`.invoice-footer-note`). */
    noteColor?: string;
    /** Compliance box text color (`.invoice-compliance-footer`). */
    complianceColor?: string;
    complianceBorderColor?: string;
    complianceBgColor?: string;
  };
  /** Order of extra logo ids for header rendering. */
  extraOrder?: string[];
  invoiceHeader: {
    /** When true, company block appears left of QUOTE/INVOICE title column. */
    companyFirst: boolean;
    /** company-details text alignment */
    companyTextAlign: 'left' | 'right';
  };
  colors: {
    headerBg: string;
    accentBorder: string;
    tableHeadBg: string;
    tableHeadText: string;
    metaCardBg: string;
    metaCardBorder: string;
    metaTitleColor: string;
    docTitleColor: string;
    bodyMutedColor: string;
  };
  type: {
    docTitlePx: number;
    bodyPx: number;
    tablePx: number;
    metaRadiusPx: number;
    stripeWidthPx: number;
    headerPaddingYPx: number;
    headerHeightPx: number;
  };
  /** Optional partner / certification marks — positioned in premium header beside main logo & badges. */
  extras: PdfExtraLogo[];
}

function clamp01(n: number): number {
  if (Number.isNaN(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

function clamp(n: number, lo: number, hi: number): number {
  if (Number.isNaN(n)) return lo;
  return Math.min(hi, Math.max(lo, n));
}

export const PDF_LAYOUT_MAX_EXTRAS = 8;
const MAX_EXTRA_DATA_URL_LEN = 620_000;

function newExtraLogoId(soFar: PdfExtraLogo[]): string {
  const c = typeof globalThis !== 'undefined' && globalThis.crypto && 'randomUUID' in globalThis.crypto
    ? globalThis.crypto.randomUUID()
    : '';
  if (c) return c;
  return `extra-${soFar.length}-${Date.now().toString(36)}`;
}

function sanitizeExtraDataUrl(raw: unknown): string | null {
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!s || s.length > MAX_EXTRA_DATA_URL_LEN) return null;
  if (!/^data:image\/(png|jpeg|gif|webp);base64,/i.test(s)) return null;
  return s;
}

function parseExtrasArray(raw: unknown): PdfExtraLogo[] {
  if (!Array.isArray(raw)) return [];
  const out: PdfExtraLogo[] = [];
  for (const item of raw) {
    if (out.length >= PDF_LAYOUT_MAX_EXTRAS) break;
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    const dataUrl = sanitizeExtraDataUrl(rec.dataUrl);
    if (!dataUrl) continue;
    const idRaw = typeof rec.id === 'string' ? rec.id.trim() : '';
    const id =
      idRaw &&
      /^[a-zA-Z0-9_-]+$/.test(idRaw) &&
      idRaw.length <= 80
        ? idRaw
        : newExtraLogoId(out);
    let xFrac = Number(rec.xFrac);
    let yFrac = Number(rec.yFrac);
    let slotWidthFrac = Number(rec.slotWidthFrac);
    if (Number.isNaN(xFrac)) xFrac = 0.72;
    if (Number.isNaN(yFrac)) yFrac = 0.5;
    if (Number.isNaN(slotWidthFrac)) slotWidthFrac = 0.14;
    out.push({
      id,
      dataUrl,
      xFrac: clamp01(xFrac),
      yFrac: clamp01(yFrac),
      slotWidthFrac: clamp(slotWidthFrac, 0.06, 0.42)
    });
  }
  return out;
}

function newLayoutId(prefix: string, soFar: number): string {
  const c =
    typeof globalThis !== 'undefined' && globalThis.crypto && 'randomUUID' in globalThis.crypto
      ? globalThis.crypto.randomUUID()
      : '';
  if (c) return c;
  return `${prefix}-${soFar}-${Date.now().toString(36)}`;
}

function parseTableColumnKey(raw: unknown): PdfTableColumnKey | null {
  const s = String(raw ?? '').trim();
  if (
    s === 'item' ||
    s === 'description' ||
    s === 'quantity' ||
    s === 'unitPrice' ||
    s === 'amount' ||
    s === 'custom'
  ) {
    return s;
  }
  return null;
}

function parseTableColumns(raw: unknown): PdfTableColumn[] {
  if (!Array.isArray(raw) || raw.length === 0) return [...DEFAULT_TABLE_COLUMNS];
  const out: PdfTableColumn[] = [];
  for (const item of raw) {
    if (out.length >= PDF_LAYOUT_MAX_TABLE_COLUMNS) break;
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    const key = parseTableColumnKey(rec.key);
    if (!key) continue;
    const idRaw = typeof rec.id === 'string' ? rec.id.trim() : '';
    const id =
      idRaw && /^[a-zA-Z0-9_-]+$/.test(idRaw) && idRaw.length <= 80
        ? idRaw
        : newLayoutId('col', out.length);
    const labelRaw = typeof rec.label === 'string' ? rec.label.trim() : '';
    const label = labelRaw.slice(0, 40) || key;
    const align =
      rec.align === 'center' || rec.align === 'right' || rec.align === 'left' ? rec.align : undefined;
    const customText =
      typeof rec.customText === 'string' ? rec.customText.trim().slice(0, 120) : undefined;
    out.push({
      id,
      key,
      label,
      align,
      showDescriptionUnderItem: key === 'item' ? rec.showDescriptionUnderItem !== false : undefined,
      customText: key === 'custom' ? customText || '' : undefined
    });
  }
  return out.length > 0 ? out : [...DEFAULT_TABLE_COLUMNS];
}

function parseBodySectionKind(raw: unknown): PdfBodySectionKind | null {
  const s = String(raw ?? '').trim();
  if (
    s === 'title' ||
    s === 'meta' ||
    s === 'itemsTable' ||
    s === 'totals' ||
    s === 'intro' ||
    s === 'signature' ||
    s === 'disclaimer' ||
    s === 'compliance' ||
    s === 'footerStrip' ||
    s === 'customText'
  ) {
    return s;
  }
  return null;
}

function parseBodySections(raw: unknown): PdfBodySection[] {
  if (!Array.isArray(raw) || raw.length === 0) return [...DEFAULT_BODY_SECTIONS];
  const out: PdfBodySection[] = [];
  for (const item of raw) {
    if (out.length >= PDF_LAYOUT_MAX_BODY_SECTIONS) break;
    if (!item || typeof item !== 'object') continue;
    const rec = item as Record<string, unknown>;
    const kind = parseBodySectionKind(rec.kind);
    if (!kind) continue;
    const idRaw = typeof rec.id === 'string' ? rec.id.trim() : '';
    const id =
      idRaw && /^[a-zA-Z0-9_-]+$/.test(idRaw) && idRaw.length <= 80
        ? idRaw
        : newLayoutId('sec', out.length);
    const title = typeof rec.title === 'string' ? rec.title.trim().slice(0, 80) : undefined;
    const text = typeof rec.text === 'string' ? rec.text.trim().slice(0, 2000) : undefined;
    out.push({
      id,
      kind,
      enabled: rec.enabled !== false,
      title:
        kind === 'customText' || kind === 'intro' || kind === 'signature'
          ? title || (kind === 'customText' ? 'Section' : kind === 'signature' ? 'Authorization' : undefined)
          : title,
      text: kind === 'customText' ? text || '' : undefined
    });
  }
  return out.length > 0 ? out : [...DEFAULT_BODY_SECTIONS];
}

function parseSectionOrder(raw: unknown, sections: PdfBodySection[]): string[] {
  const ids = sections.map((s) => s.id);
  if (!Array.isArray(raw)) return ids;
  const ordered: string[] = [];
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const id = item.trim();
    if (ids.includes(id) && !ordered.includes(id)) ordered.push(id);
  }
  for (const id of ids) {
    if (!ordered.includes(id)) ordered.push(id);
  }
  return ordered;
}

export function resolveBodySections(layout: PdfCustomLayout): PdfBodySection[] {
  const sections = layout.body?.sections?.length
    ? layout.body.sections
    : [...DEFAULT_BODY_SECTIONS];
  const order = layout.body?.sectionOrder?.length
    ? parseSectionOrder(layout.body.sectionOrder, sections)
    : sections.map((s) => s.id);
  return order
    .map((id) => sections.find((s) => s.id === id))
    .filter((s): s is PdfBodySection => Boolean(s));
}

export function resolveTableColumns(layout: PdfCustomLayout): PdfTableColumn[] {
  return layout.body?.tableColumns?.length
    ? layout.body.tableColumns
    : [...DEFAULT_TABLE_COLUMNS];
}

export function sanitizeHex(hex: unknown, fb: string): string {
  const s = typeof hex === 'string' ? hex.trim() : '';
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s);
  if (!m) return fb;
  return `#${m[1]!.length === 3 ? m[1]!.split('').map((c) => c + c).join('').toLowerCase() : m[1]!.toLowerCase()}`;
}

export function logoXFracFromAlign(align: LogoAlign, slotWidthFrac: number): number {
  if (align === 'center') return Math.max(0, 0.5 - slotWidthFrac / 2);
  if (align === 'right') return Math.max(0, 1 - slotWidthFrac - 0.03);
  return 0.03;
}

export function defaultPdfCustomLayout(brandColorFallback: string): PdfCustomLayout {
  const accent = sanitizeHex(brandColorFallback, '#2d8dcc');
  return {
    v: 1,
    logo: {
      xFrac: 0.03,
      yFrac: 0.12,
      slotWidthFrac: 0.36,
      align: 'left'
    },
    badges: {
      rightInsetFrac: 0.03,
      yFrac: 0.14,
      gapPx: 8,
      showCfaa: true,
      showAcmo: true
    },
    stripe: { show: true },
    body: {
      rowAltBg: '#f8fafc',
      tableColumns: [...DEFAULT_TABLE_COLUMNS],
      sections: [...DEFAULT_BODY_SECTIONS],
      sectionOrder: DEFAULT_BODY_SECTIONS.map((s) => s.id)
    },
    footer: {
      showLogoStrip: false,
      noteColor: '#64748b',
      complianceColor: accent,
      complianceBorderColor: accent,
      complianceBgColor: '#ffffff'
    },
    invoiceHeader: {
      companyFirst: false,
      companyTextAlign: 'right'
    },
    colors: {
      headerBg: accent,
      accentBorder: accent,
      tableHeadBg: '#f1f5f9',
      tableHeadText: '#334155',
      metaCardBg: '#f8fafc',
      metaCardBorder: '#cbd5e1',
      metaTitleColor: '#334155',
      docTitleColor: '#0f172a',
      bodyMutedColor: '#64748b'
    },
    type: {
      docTitlePx: 28,
      bodyPx: 12,
      tablePx: 12,
      metaRadiusPx: 12,
      stripeWidthPx: 6,
      headerPaddingYPx: 16,
      headerHeightPx: 126
    },
    extras: []
  };
}

export function parsePdfLayoutJson(
  raw: string | undefined | null,
  brandColorFallback: string
): PdfCustomLayout {
  let partial: Partial<PdfCustomLayout> | null = null;
  const s = String(raw ?? '').trim();
  if (s) {
    try {
      partial = JSON.parse(s) as Partial<PdfCustomLayout>;
    } catch {
      partial = null;
    }
  }
  const fb = defaultPdfCustomLayout(brandColorFallback);
  if (!partial || partial.v !== 1) return fb;
  const logoIn = (partial.logo || {}) as Partial<PdfCustomLayout['logo']>;
  const badgesIn = (partial.badges || {}) as Partial<PdfCustomLayout['badges']> & { topFracOfHeader?: number };
  const hdrIn = (partial.invoiceHeader || {}) as Partial<PdfCustomLayout['invoiceHeader']>;
  const colIn = (partial.colors || {}) as Partial<PdfCustomLayout['colors']>;
  const typIn = (partial.type || {}) as Partial<PdfCustomLayout['type']>;
  const extrasParsed = parseExtrasArray(partial.extras);
  const stripeIn = (partial.stripe || {}) as Partial<NonNullable<PdfCustomLayout['stripe']>>;
  const bodyIn = (partial.body || {}) as Partial<NonNullable<PdfCustomLayout['body']>>;
  const footerIn = (partial.footer || {}) as Partial<NonNullable<PdfCustomLayout['footer']>>;
  const logoAlign: LogoAlign =
    logoIn.align === 'center' || logoIn.align === 'right' || logoIn.align === 'left'
      ? logoIn.align
      : fb.logo.align ?? 'left';
  const slotWidthFrac = clamp(logoIn.slotWidthFrac ?? fb.logo.slotWidthFrac, 0.1, 0.6);
  const xFrac =
    logoIn.xFrac != null && logoIn.align == null
      ? clamp01(logoIn.xFrac)
      : logoXFracFromAlign(logoAlign, slotWidthFrac);
  return {
    v: 1,
    logo: {
      xFrac,
      yFrac: clamp01(logoIn.yFrac ?? fb.logo.yFrac),
      slotWidthFrac,
      align: logoAlign
    },
    badges: {
      rightInsetFrac: clamp01(badgesIn.rightInsetFrac ?? fb.badges.rightInsetFrac),
      yFrac: clamp01(badgesIn.yFrac ?? badgesIn.topFracOfHeader ?? fb.badges.yFrac),
      gapPx: clamp(Math.round(Number(badgesIn.gapPx)), 0, 24),
      showCfaa: badgesIn.showCfaa !== false,
      showAcmo: badgesIn.showAcmo !== false
    },
    stripe: {
      show: stripeIn.show !== false
    },
    body: (() => {
      const sections = parseBodySections(bodyIn.sections ?? fb.body?.sections);
      return {
        rowAltBg: sanitizeHex(bodyIn.rowAltBg, fb.body?.rowAltBg ?? '#f8fafc'),
        tableColumns: parseTableColumns(bodyIn.tableColumns ?? fb.body?.tableColumns),
        sections,
        sectionOrder: parseSectionOrder(bodyIn.sectionOrder ?? fb.body?.sectionOrder, sections)
      };
    })(),
    footer: {
      showLogoStrip: Boolean(footerIn.showLogoStrip ?? fb.footer?.showLogoStrip),
      noteColor: sanitizeHex(footerIn.noteColor, fb.footer?.noteColor ?? '#64748b'),
      complianceColor: sanitizeHex(
        footerIn.complianceColor,
        fb.footer?.complianceColor ?? fb.colors.accentBorder
      ),
      complianceBorderColor: sanitizeHex(
        footerIn.complianceBorderColor,
        fb.footer?.complianceBorderColor ?? fb.colors.accentBorder
      ),
      complianceBgColor: sanitizeHex(footerIn.complianceBgColor, fb.footer?.complianceBgColor ?? '#ffffff')
    },
    invoiceHeader: {
      companyFirst: Boolean(hdrIn.companyFirst ?? fb.invoiceHeader.companyFirst),
      companyTextAlign:
        hdrIn.companyTextAlign === 'left'
          ? 'left'
          : hdrIn.companyTextAlign === 'right'
            ? 'right'
            : fb.invoiceHeader.companyTextAlign
    },
    colors: {
      headerBg: sanitizeHex(colIn.headerBg, fb.colors.headerBg),
      accentBorder: sanitizeHex(colIn.accentBorder, fb.colors.accentBorder),
      tableHeadBg: sanitizeHex(colIn.tableHeadBg, fb.colors.tableHeadBg),
      tableHeadText: sanitizeHex(colIn.tableHeadText, fb.colors.tableHeadText),
      metaCardBg: sanitizeHex(colIn.metaCardBg, fb.colors.metaCardBg),
      metaCardBorder: sanitizeHex(colIn.metaCardBorder, fb.colors.metaCardBorder),
      metaTitleColor: sanitizeHex(colIn.metaTitleColor, fb.colors.metaTitleColor),
      docTitleColor: sanitizeHex(colIn.docTitleColor, fb.colors.docTitleColor),
      bodyMutedColor: sanitizeHex(colIn.bodyMutedColor, fb.colors.bodyMutedColor)
    },
    type: {
      docTitlePx: clamp(typIn.docTitlePx ?? fb.type.docTitlePx, 18, 40),
      bodyPx: clamp(typIn.bodyPx ?? fb.type.bodyPx, 10, 16),
      tablePx: clamp(typIn.tablePx ?? fb.type.tablePx, 10, 15),
      metaRadiusPx: clamp(typIn.metaRadiusPx ?? fb.type.metaRadiusPx, 4, 24),
      stripeWidthPx: clamp(typIn.stripeWidthPx ?? fb.type.stripeWidthPx, 2, 16),
      headerPaddingYPx: clamp(typIn.headerPaddingYPx ?? fb.type.headerPaddingYPx, 8, 32),
      headerHeightPx: clamp(typIn.headerHeightPx ?? fb.type.headerHeightPx, 96, 180)
    },
    extras: extrasParsed,
    extraOrder: Array.isArray(partial.extraOrder)
      ? (partial.extraOrder as unknown[]).filter((id): id is string => typeof id === 'string')
      : extrasParsed.map((e) => e.id)
  };
}

/**
 * Overrides placed after base PDF styles — uses !important where needed against existing rules.
 */
export function pdfCustomLayoutCss(
  layout: PdfCustomLayout,
  opts: { usePremiumHeader: boolean; scopePrefix?: string; weakenHeaderChrome?: boolean }
): string {
  const sc = opts.scopePrefix?.trim()
    ? `${opts.scopePrefix.trim()} `
    : '';
  const { colors, type } = layout;
  const L = layout.logo;
  const B = layout.badges;
  const H = layout.invoiceHeader;
  const bgImp = opts.weakenHeaderChrome ? '' : ' !important';
  const stripeImp = opts.weakenHeaderChrome ? '' : ' !important';

  const headerChrome = opts.usePremiumHeader
    ? `
    ${sc}.pdf-brand-header {
      height: ${type.headerHeightPx}px !important;
      padding: ${type.headerPaddingYPx}px 24px !important;
      background: ${colors.headerBg}${bgImp};
      display: block !important;
      position: relative !important;
    }
    ${sc}.pdf-header-logo-slot {
      position: absolute !important;
      left: calc(${L.xFrac * 100}% ) !important;
      top: calc(${L.yFrac * 100}% ) !important;
      width: calc(${L.slotWidthFrac * 100}% ) !important;
      transform: translate(0, 0);
    }
    ${sc}.pdf-header-logo-slot img.pdf-brand-logo-img {
      width: 100% !important;
      max-width: 100% !important;
      max-height: 92px !important;
      height: auto !important;
      object-fit: contain !important;
      display: block !important;
    }
    ${sc}.pdf-header-badges-slot {
      position: absolute !important;
      top: calc(${B.yFrac * 100}% ) !important;
      right: calc(${B.rightInsetFrac * 100}% ) !important;
      display: flex !important;
      flex-direction: column !important;
      align-items: flex-end !important;
      gap: ${B.gapPx}px !important;
    }
    ${sc}.pdf-brand-member { max-height: 36px !important; margin: 0 !important; margin-left: 0 !important; }
    ${sc}.pdf-extra-logo-slot {
      box-sizing: border-box !important;
      z-index: 2 !important;
    }
    ${sc}.pdf-extra-logo-slot img.pdf-extra-logo-img {
      width: 100% !important;
      max-width: 100% !important;
      max-height: 56px !important;
      height: auto !important;
      object-fit: contain !important;
      display: block !important;
    }
    `
    : `
    ${sc}.pdf-brand-header .pdf-header-logo-slot { position:relative !important; left:auto !important; top:auto !important; width:auto !important; }
    ${sc}.pdf-brand-header .pdf-header-badges-slot { position:relative !important; right:auto !important; top:auto !important; align-items:flex-end !important; }
    `;

  const stripeShow = layout.stripe?.show !== false;
  const rowAlt = layout.body?.rowAltBg ?? '#f8fafc';

  return `
${headerChrome}
${sc}.pdf-body-content {
  border-left: ${stripeShow ? `${type.stripeWidthPx}px solid ${colors.accentBorder}` : 'none'}${stripeImp};
  font-size: ${type.bodyPx}px !important;
}
${sc}.invoice-table tbody tr:nth-child(even) td {
  background: ${rowAlt} !important;
}
${sc}.pdf-footer-logo-strip {
  display: ${layout.footer?.showLogoStrip ? 'flex' : 'none'} !important;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
}
${sc}.pdf-footer-logo-strip img {
  max-height: 28px;
  object-fit: contain;
}
${sc}.invoice-title {
  font-size: ${type.docTitlePx}px !important;
  color: ${colors.docTitleColor} !important;
}
${sc}.invoice-header {
  flex-direction: ${H.companyFirst ? 'row-reverse' : 'row'} !important;
}
${sc}.company-details {
  text-align: ${H.companyTextAlign} !important;
  color: ${colors.bodyMutedColor} !important;
  font-size: ${type.bodyPx}px !important;
}
${sc}table.invoice-table { font-size: ${type.tablePx}px !important; }
${sc}.invoice-table thead th {
  background: ${colors.tableHeadBg} !important;
  color: ${colors.tableHeadText} !important;
}
${sc}.meta-group {
  background: ${colors.metaCardBg} !important;
  border-color: ${colors.metaCardBorder} !important;
  border-radius: ${type.metaRadiusPx}px !important;
}
${sc}.meta-group h3 {
  color: ${colors.metaTitleColor} !important;
}
${sc}.invoice-footer-note {
  color: ${layout.footer?.noteColor ?? '#64748b'} !important;
}
${sc}.invoice-compliance-footer {
  color: ${layout.footer?.complianceColor ?? colors.accentBorder} !important;
  border-color: ${layout.footer?.complianceBorderColor ?? colors.accentBorder} !important;
  background: ${layout.footer?.complianceBgColor ?? '#ffffff'} !important;
}
`;
}
